import { dedupeUses, normalizeUse, pickObject } from './data';
import { timing, variantForLevel, } from './params';
export function makeStimulus(trialIdx, durationMs, level, lang) {
    const variant = variantForLevel(level);
    const clampedDur = Math.max(30_000, Math.min(90_000, Math.round(durationMs)));
    const object = pickObject(lang, variant.familiarity, trialIdx + 1);
    return {
        trialIdx,
        durationMs: clampedDur,
        object,
        lang,
        targetFluidity: variant.targetFluidity,
    };
}
export function createAlternativeUsesAdapter(level = 1, lang = 'fr') {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
        uses: [],
        remainingMs: 0,
    };
    void lang;
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    let tickInterval = null;
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
        if (tickInterval !== null) {
            clearInterval(tickInterval);
            tickInterval = null;
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(stim) {
        if (!resolver)
            return;
        const fluidity = state.uses.length;
        const r = resolver;
        resolver = null;
        rejecter = null;
        clearTimers();
        r({
            uses: [...state.uses],
            fluidity,
            reactionTimeMs: Date.now() - trialStart,
            correct: fluidity >= stim.targetFluidity,
        });
    }
    return {
        mount() {
            // Idempotent.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'priming',
                    stim,
                    feedback: null,
                    uses: [],
                    remainingMs: stim.durationMs,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'generate' });
                    trialStart = Date.now();
                    tickInterval = setInterval(() => {
                        const elapsed = Date.now() - trialStart;
                        const remaining = Math.max(0, stim.durationMs - elapsed);
                        if (remaining <= 0) {
                            if (tickInterval !== null) {
                                clearInterval(tickInterval);
                                tickInterval = null;
                            }
                            if (state.subPhase === 'generate') {
                                update({ remainingMs: 0 });
                                settle(stim);
                            }
                        }
                        else {
                            update({ remainingMs: remaining });
                        }
                    }, 250);
                    timers.push(setTimeout(() => {
                        if (state.subPhase === 'generate') {
                            settle(stim);
                        }
                    }, stim.durationMs + 100));
                }, timing.primingMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        submitUse(input) {
            if (state.subPhase !== 'generate' || state.stim === null) {
                return { accepted: false, reason: 'phase' };
            }
            const norm = normalizeUse(input);
            if (norm.length < 2) {
                return { accepted: false, reason: 'too-short' };
            }
            const next = dedupeUses([...state.uses, norm]);
            if (next.length === state.uses.length) {
                return { accepted: false, reason: 'duplicate' };
            }
            update({ uses: next });
            return { accepted: true };
        },
        finishRound() {
            if (state.subPhase !== 'generate' || state.stim === null)
                return;
            settle(state.stim);
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map